import { useState, useEffect, useCallback } from 'react';
import { Loader2, Plus, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import type { StockPlanningItem, ColumnPreference } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Pagination } from '../../ui/pagination';
import StockPlanningTable from './StockPlanningTable';
import ColumnManager from './ColumnManager';
import StockPlanningFilters from './StockPlanningFilters';
import CallOffWizard from '../management/CallOffWizard';
import StockStatusSubTab from '../sub-tabs/StockStatusSubTab';
import OpenPOSubTab from '../sub-tabs/OpenPOSubTab';
import SALCommittedSubTab from '../sub-tabs/SALCommittedSubTab';
import PolishFactorySubTab from '../sub-tabs/PolishFactorySubTab';
import MinMaxSubTab from '../sub-tabs/MinMaxSubTab';
import ForecastSubTab from '../sub-tabs/ForecastSubTab';
import PolandStockSubTab from '../sub-tabs/PolandStockSubTab';
import InTransitSubTab from '../sub-tabs/InTransitSubTab';
import { toast } from 'sonner';
import { useCallOffConfig } from '../../../hooks/useCallOffConfig';

/**
 * Pull a readable message out of a failed blob download.
 *
 * With `responseType: 'blob'` an axios error body is a Blob, so the usual
 * `err.message` yields only "Request failed with status code 400" and the
 * server's explanation is lost. Reads the blob back as JSON when present.
 *
 * @param err - The rejected axios error
 * @param fallback - Message to use when nothing better can be recovered
 * @returns A message safe to show the user
 */
async function blobErrorMessage(err: unknown, fallback: string): Promise<string> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      const detail = parsed?.detail ?? parsed?.message;
      if (typeof detail === 'string' && detail) return detail;
    } catch {
      // Not JSON (an HTML error page, or an empty body) — fall through.
    }
  }
  return err instanceof Error ? err.message : fallback;
}

interface StockPlanningFiltersState {
  supplierId: string;
  openPoDays: number;
  salCommittedEndDate: string;
  factoryFilter: string;
  commodityType: string;
}

export default function StockPlanningTab() {
  const { user } = useAuth();
  const isCustomer = user?.company_type === 'customer' || user?.platform === 'CUSTOMER';

  // CR-6176 / CR-1154: open the PO Lines tab pre-filtered to this part number in
  // a NEW TAB, so the user can compare stock planning and the part's PO lines
  // side by side. The PO Lines tab reads `partNumber` from the URL on mount (see
  // usePOLinesState); a fresh tab recovers the session via the CR-6282
  // BroadcastChannel handoff.
  const handlePartNumberClick = (partNumber: string) => {
    const params = new URLSearchParams({ tab: 'po_lines', partNumber });
    window.open(`/purchasing?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };
  const { config } = useCallOffConfig();
  const [items, setItems] = useState<StockPlanningItem[]>([]);
  const [columns, setColumns] = useState<ColumnPreference[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState<StockPlanningFiltersState>({
    supplierId: '',
    openPoDays: 7,
    salCommittedEndDate: '',
    factoryFilter: '',
    commodityType: '',
  });
  // Hide rows whose part_number or description starts with the literal "part"
  // (case-insensitive). Mirrors the supplier-side Current Stock filter on the
  // call-off stock-upload grid. Applied client-side over the current page.
  const [hideTestParts, setHideTestParts] = useState(false);

  const fetchData = useCallback(async () => {
    // Don't fetch data until a supplier is selected
    if (!filters.supplierId) {
      setItems([]);
      setTotalCount(0);
      return;
    }

    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        page,
        page_size: pageSize,
        supplier_id: filters.supplierId,
      };
      if (filters.openPoDays) params['open_po_days'] = filters.openPoDays;
      if (filters.salCommittedEndDate) params['sal_committed_end_date'] = filters.salCommittedEndDate;
      if (filters.factoryFilter) params['factory_filter'] = filters.factoryFilter;
      if (filters.commodityType) params['commodity_type'] = filters.commodityType;

      const [dataRes, colRes] = await Promise.all([
        callOffAPI.getStockPlanning(params),
        callOffAPI.getColumns(),
      ]);

      setItems(dataRes.data.items);
      setTotalCount(dataRes.data.total_count);
      setColumns(colRes.data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load stock planning data';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleColumn = async (columnId: string) => {
    try {
      await callOffAPI.toggleColumn(columnId);
      const res = await callOffAPI.getColumns();
      setColumns(res.data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to toggle column';
      toast.error(message);
    }
  };

  const handleSortColumns = async (updatedColumns: { column_id: string; sort_order: number }[]) => {
    try {
      const res = await callOffAPI.sortColumns(updatedColumns);
      setColumns(res.data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to sort columns';
      toast.error(message);
    }
  };

  const handleExport = async (file_format: 'csv' | 'xlsx') => {
    if (!filters.supplierId) {
      toast.error('Pick a supplier before exporting.');
      return;
    }
    setExporting(true);
    try {
      // Send the *currently visible* columns in display order so the file
      // matches what's on screen — including hide/reorder choices made via
      // the Columns dropdown.
      const visibleIds = columns
        .filter((c) => c.is_visible)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((c) => c.column_id)
        .join(',');
      const response = await callOffAPI.exportStockPlanning({
        file_format,
        supplier_id: Number(filters.supplierId),
        columns: visibleIds,
        open_po_days: filters.openPoDays,
        sal_committed_end_date: filters.salCommittedEndDate || undefined,
        factory_filter: filters.factoryFilter || undefined,
        commodity_type: filters.commodityType || undefined,
      });
      const mime =
        file_format === 'csv'
          ? 'text/csv'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock_planning_${new Date().toISOString().split('T')[0]}.${file_format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${file_format.toUpperCase()}`);
    } catch (err: unknown) {
      toast.error(await blobErrorMessage(err, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  /**
   * CR-7163 (c): the supplier's warehouse stock report, restored to Stock
   * Planning. Distinct from handleExport above — that exports this grid's
   * columns; this pulls the supplier's actual stock levels, the same file the
   * supplier gets from Stock Upload.
   */
  const handleExportSupplierStock = async (file_format: 'csv' | 'xlsx') => {
    if (!filters.supplierId) {
      toast.error('Pick a supplier before exporting.');
      return;
    }
    setExporting(true);
    try {
      const response = await callOffAPI.exportStock(
        file_format,
        Number(filters.supplierId),
      );
      const mime =
        file_format === 'csv'
          ? 'text/csv'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `supplier_stock_${new Date().toISOString().split('T')[0]}.${file_format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${file_format.toUpperCase()}`);
    } catch (err: unknown) {
      toast.error(await blobErrorMessage(err, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const [subTab, setSubTab] = useState('stock_status');
  // Selected part filters sub-tab data; will be set when a row is clicked in the grid
  const [selectedPart] = useState<string | undefined>(undefined);

  const visibleColumns = columns.filter((c) => c.is_visible).sort((a, b) => a.sort_order - b.sort_order);
  const totalPages = Math.ceil(totalCount / pageSize);

  const visibleItems = hideTestParts
    ? items.filter(
        (it) =>
          !(it.part_number || '').toLowerCase().startsWith('part') &&
          !(it.description || '').toLowerCase().startsWith('part'),
      )
    : items;

  return (
    <div className="space-y-4">
      {/* CR-6194: the filter panel is now a sectioned card, so it sits as its
          own full-width block above the toolbar rather than inline beside the
          Parts select — a card can't share a `flex items-end` row with a bare
          control without the baselines going out. */}
      <StockPlanningFilters
        filters={filters}
        onFiltersChange={(f) => { setFilters(f); setPage(1); }}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-end gap-4">
          <div className="space-y-1">
            <Label className="text-xs">Parts</Label>
            <select
              value={hideTestParts ? 'hide_part' : 'all'}
              onChange={(e) => setHideTestParts(e.target.value === 'hide_part')}
              className="w-44 px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
              aria-label="Row filter"
            >
              <option value="all">Show all parts</option>
              <option value="hide_part">Hide &ldquo;part&rdquo;-prefixed</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" disabled={!filters.supplierId || exporting}>
                {exporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Export
              </Button>
            </DropdownMenuTrigger>
            {/* CR-7163 (c): two distinct reports, one control. Labelled
                groups rather than a second Export button, so "Export" doesn't
                appear twice meaning different things. */}
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Stock planning grid
              </DropdownMenuLabel>
              <DropdownMenuItem
                disabled={!filters.supplierId || exporting}
                onClick={() => handleExport('xlsx')}
              >
                <FileSpreadsheet className="h-4 w-4 mr-2 text-green-700" />
                Download Excel
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!filters.supplierId || exporting}
                onClick={() => handleExport('csv')}
              >
                <FileText className="h-4 w-4 mr-2 text-blue-700" />
                Download CSV
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Supplier warehouse stock
              </DropdownMenuLabel>
              <DropdownMenuItem
                disabled={!filters.supplierId || exporting}
                onClick={() => handleExportSupplierStock('xlsx')}
              >
                <FileSpreadsheet className="h-4 w-4 mr-2 text-green-700" />
                Download Excel
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!filters.supplierId || exporting}
                onClick={() => handleExportSupplierStock('csv')}
              >
                <FileText className="h-4 w-4 mr-2 text-blue-700" />
                Download CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ColumnManager
            columns={columns}
            onToggle={handleToggleColumn}
            onSort={handleSortColumns}
          />
          {isCustomer && (
            <Button className="gap-2" onClick={() => setShowNewDialog(true)}>
              <Plus className="h-4 w-4" />
              New Call Off
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            Stock Planning Grid
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({hideTestParts ? `${visibleItems.length} of ${totalCount}` : totalCount} parts)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filters.supplierId && (
            <div className="mb-3 border-b pb-3">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalCount={totalCount}
                itemsPerPage={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                itemLabel="parts"
              />
            </div>
          )}
          {!filters.supplierId ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              Select a supplier to view stock planning data
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : hideTestParts && visibleItems.length === 0 && items.length > 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              All {items.length} rows on this page were hidden by the active filter.
            </div>
          ) : (
            <StockPlanningTable items={visibleItems} columns={visibleColumns} onPartNumberClick={handlePartNumberClick} />
          )}

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            itemLabel="parts"
          />
        </CardContent>
      </Card>

      {/* Sub-tabs for detailed stock planning views */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Detail Views</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={subTab} onValueChange={setSubTab}>
            <TabsList className="flex flex-wrap h-auto gap-1">
              <TabsTrigger value="stock_status" className="text-xs">Stock Status</TabsTrigger>
              <TabsTrigger value="open_po" className="text-xs">Open PO</TabsTrigger>
              <TabsTrigger value="sal_committed" className="text-xs">SAL Committed</TabsTrigger>
              {config.polish_factory_subtab_enabled && (
                <TabsTrigger value="polish_factory" className="text-xs">Polish Factory</TabsTrigger>
              )}
              <TabsTrigger value="min_max" className="text-xs">Min/Max</TabsTrigger>
              <TabsTrigger value="forecast" className="text-xs">Forecast</TabsTrigger>
              <TabsTrigger value="poland_stock" className="text-xs">Poland Stock</TabsTrigger>
              <TabsTrigger value="in_transit" className="text-xs">In Transit</TabsTrigger>
            </TabsList>

            <div className="mt-4">
              <TabsContent value="stock_status"><StockStatusSubTab partNumber={selectedPart} /></TabsContent>
              <TabsContent value="open_po"><OpenPOSubTab partNumber={selectedPart} /></TabsContent>
              <TabsContent value="sal_committed"><SALCommittedSubTab partNumber={selectedPart} /></TabsContent>
              {config.polish_factory_subtab_enabled && (
                <TabsContent value="polish_factory"><PolishFactorySubTab partNumber={selectedPart} /></TabsContent>
              )}
              <TabsContent value="min_max"><MinMaxSubTab partNumber={selectedPart} /></TabsContent>
              <TabsContent value="forecast"><ForecastSubTab partNumber={selectedPart} /></TabsContent>
              <TabsContent value="poland_stock"><PolandStockSubTab partNumber={selectedPart} /></TabsContent>
              <TabsContent value="in_transit"><InTransitSubTab partNumber={selectedPart} /></TabsContent>
            </div>
          </Tabs>
        </CardContent>
      </Card>

      <CallOffWizard
        open={showNewDialog}
        onClose={() => setShowNewDialog(false)}
        onCreated={fetchData}
        initialSupplierId={filters.supplierId ? Number(filters.supplierId) : null}
        initialOpenPoDays={filters.openPoDays}
      />
    </div>
  );
}
